// ===========================================
// Kanban Card - Draggable Project Card for Kanban Board
// ===========================================

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { 
  FolderKanban, 
  Clock, 
  DollarSign,
  TrendingUp,
  GripVertical
} from 'lucide-react';

const KanbanCard = ({ project, isDragging }) => {
  const navigate = useNavigate();
  
  // Setup sortable drag functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging
  } = useSortable({ 
    id: project._id || project.id,
    data: { project }
  });

  // Apply transform styles for drag animation
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1
  };

  // Format relative time for last focused
  const getRelativeTime = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Navigate to project detail on click
  // Note: Click handler is on the content div to avoid interference with dnd-kit's
  // event handling on the sortable element (setNodeRef element)
  const handleClick = () => {
    navigate(`/dashboard/projects/${project._id || project.id}`);
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`kanban-card ${isSortableDragging ? 'kanban-card--dragging' : ''}`}
    >
      {/* Drag Handle - only this element triggers dragging */}
      <div 
        className="kanban-card__drag-handle"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={14} />
      </div>

      {/* Card Content - clickable area to navigate to project */}
      <div className="kanban-card__content" onClick={handleClick}>
        {/* Header with icon and time */}
        <div className="kanban-card__header">
          <div className="kanban-card__icon">
            <FolderKanban size={14} />
          </div>
          <span className="kanban-card__time">
            <Clock size={10} />
            {getRelativeTime(project.lastFocused)}
          </span>
        </div>

        {/* Project Name */}
        <h4 className="kanban-card__name">{project.name}</h4>

        {/* Description (truncated) */}
        {project.description && (
          <p className="kanban-card__description">
            {project.description.length > 80 
              ? `${project.description.substring(0, 80)}...` 
              : project.description}
          </p>
        )}

        {/* Footer with cost/ARR info */}
        <div className="kanban-card__footer">
          {/* ARR Badge */}
          {project.arrLevel && project.arrLevel !== '$0' && (
            <span className="kanban-card__arr">
              <TrendingUp size={12} />
              {project.arrLevel}
            </span>
          )}
          
          {/* Monthly Cost */}
          {(project.totalMonthlyCost > 0 || project.aiDevTokenSpend > 0) && (
            <span className="kanban-card__cost">
              <DollarSign size={12} />
              {((project.totalMonthlyCost || 0) + (project.aiDevTokenSpend || 0)).toFixed(0)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;

